/**
 * Shared pieces of the administrator console.
 *
 * Every console list needs the same three things, so they are defined once here:
 * the page frame (breadcrumbs, title, description), a badge that maps a derived
 * state to a colour, and the two pieces of list furniture — a collapsible row
 * and a pager that speaks the server's `PagedResponse` envelope.
 *
 * A row stays two lines tall until it is opened. That is what keeps twenty
 * records and both pagers within one screen, so paging never means scrolling
 * past the whole list first.
 *
 * The console never invents its own state: application states, invite code
 * states and mail results are all derived on the server and only labelled here.
 */

import type { ReactNode } from 'react'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { ChevronDown } from 'lucide-react'

import type { ActivationCodeState } from '@shared/types'

import { PageHeader } from '../../components/layout/page-header'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { ApiError } from '../../lib/api'
import { apiErrorText } from '../../lib/errors'
import { cn } from '../../lib/format'
import i18n from '../../lib/i18n'
import { mutationErrorText } from '../../lib/mutation-error'

/**
 * Whether a suspension is currently in force.
 *
 * A suspension is stored as an expiry and nothing else, so the only rule is that
 * the expiry is still in the future. The account list and the account detail
 * share this helper instead of each deciding the question for itself.
 */
export function isBanned(bannedUntil: number | null | undefined, now = Date.now()): boolean {
  return bannedUntil !== null && bannedUntil !== undefined && bannedUntil * 1000 > now
}

/**
 * Copy for a failed console request.
 *
 * The server reports a machine readable `reason` for every console refusal — the
 * last owner, editing yourself, a request that was already decided — while the
 * generic `invalid_request` sentence says nothing about which one it was. The
 * reason is translated here; anything else, including a request that never
 * produced a response at all, falls back to the shared mutation copy.
 */
export function adminErrorText(error: unknown, t: TFunction): string | null {
  if (!(error instanceof ApiError)) {
    return mutationErrorText(error, t)
  }
  const reason = typeof error.details.reason === 'string' ? error.details.reason : null
  const key = reason ? `admin.errorReason.${reason}` : null
  if (key && i18n.exists(key)) {
    return t(key)
  }
  return apiErrorText(error, t)
}

export interface AdminPageProps {
  title: string
  description?: string
  current: string
  actions?: ReactNode
  children: ReactNode
}

/** Page frame: console breadcrumbs, a heading and optional page level actions. */
export function AdminPage({ title, description, current, actions, children }: AdminPageProps) {
  const { t } = useTranslation()
  return (
    <div className="container-narrow flex flex-col gap-5">
      <PageHeader
        breadcrumbs={[
          { label: t('nav.home'), to: '/' },
          { label: t('admin.title'), to: '/admin' },
          ...(current === t('admin.title') ? [] : [{ label: current }]),
        ]}
        title={title}
        description={description}
      />
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      {children}
    </div>
  )
}

type BadgeVariant = 'default' | 'secondary' | 'outline' | 'success' | 'warning' | 'destructive'

/** Colour for each derived invite code state. */
export function codeStateVariant(state: ActivationCodeState): BadgeVariant {
  if (state === 'unused') {
    return 'default'
  }
  if (state === 'used') {
    return 'success'
  }
  return state === 'expired' ? 'warning' : 'secondary'
}

/** Colour for the application decision states. */
export function applicationVariant(status: string): BadgeVariant {
  if (status === 'pending') {
    return 'warning'
  }
  if (status === 'approved') {
    return 'success'
  }
  if (status === 'rejected') {
    return 'destructive'
  }
  return 'secondary'
}

/** Colour for the mail delivery result. */
export function mailVariant(status: string): BadgeVariant {
  if (status === 'sent') {
    return 'success'
  }
  return status === 'failed' ? 'destructive' : 'warning'
}

export function StateBadge({
  label,
  variant = 'secondary',
}: {
  label: string
  variant?: BadgeVariant
}) {
  return <Badge variant={variant}>{label}</Badge>
}

export interface PagerProps {
  page: number
  pageSize: number
  total: number
  onPage: (page: number) => void
  className?: string
}

/** Page number and query-string writer shared by every console list. */
export interface PageParamHandle {
  /** Current page, read from `?page=` and never below one. */
  page: number
  /** The query string, for the list's own filters. */
  params: URLSearchParams
  /** Applies filter changes; an empty value deletes the parameter. */
  update: (next: Record<string, string>) => void
  /** Moves to another page, keeping every other filter. */
  goToPage: (page: number) => void
}

/**
 * The page number of a console list, read from and written to the query string.
 *
 * The page belongs in the URL so a console view can be linked to a colleague
 * exactly as it is on screen. Two rules come from the same place: a filter set to
 * the empty string deletes its parameter instead of leaving `?query=` behind, and
 * a filter change travels with `page: '1'` so a narrowed list cannot open on a
 * page that no longer exists.
 */
export function usePageParam(): PageParamHandle {
  const [params, setParams] = useSearchParams()
  const page = Number(params.get('page') ?? '1') || 1

  function update(next: Record<string, string>): void {
    const updated = new URLSearchParams(params)
    for (const [key, value] of Object.entries(next)) {
      if (value.length === 0) {
        updated.delete(key)
      } else {
        updated.set(key, value)
      }
    }
    setParams(updated, { replace: true })
  }

  function goToPage(next: number): void {
    update({ page: String(next) })
  }

  return { page, params, update, goToPage }
}

/**
 * Server-side pager.
 *
 * The page number lives in the query string of the route, so a console view can
 * be linked to a colleague exactly as it is on screen. Each list renders it
 * above and below the rows, which is what removes the scroll-to-the-bottom step
 * from paging.
 */
export function Pager({ page, pageSize, total, onPage, className }: PagerProps) {
  const { t } = useTranslation()
  const lastPage = Math.max(1, Math.ceil(total / pageSize))
  return (
    <div className={cn('flex items-center justify-between gap-3 text-sm', className)}>
      <span className="text-muted-foreground">
        {t('admin.common.pageOf', { page, total: lastPage, count: total })}
      </span>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          {t('admin.common.prev')}
        </Button>
        <Button
          variant="outline"
          size="sm"
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
        >
          {t('admin.common.next')}
        </Button>
      </div>
    </div>
  )
}

export interface CollapsibleRowProps {
  /** First line: the record's identity, always visible. */
  title: ReactNode
  /** Second line: the compact facts that fit next to the identity. */
  meta?: ReactNode
  /** State badges, right aligned on the summary line. */
  badges?: ReactNode
  /** What opening the row reveals. */
  children: ReactNode
  defaultOpen?: boolean
}

/**
 * One collapsible console record.
 *
 * The summary carries identity and state; actions and long values live in the
 * opened part. Native `details`/`summary` supplies the toggle, keyboard support
 * and the expanded state without a client store, which keeps the list a pure
 * function of the server response.
 *
 * The identity column and the badge group share one line. The identity keeps a
 * fixed floor (`basis-24`) and never shrinks, so a long name or address truncates
 * inside its own column instead of pushing the row wider than the card; the badge
 * group absorbs all the remaining shrink and wraps internally when it has to, so
 * a third badge never asks the identity to make room. Without that floor a badge
 * group sized to its own content claims the whole row and leaves the record's own
 * name a few pixels wide, spilling its text over the neighbouring column.
 */
export function CollapsibleRow({
  title,
  meta,
  badges,
  children,
  defaultOpen,
}: CollapsibleRowProps) {
  return (
    <Card>
      <details open={defaultOpen} className="group">
        <summary className="flex cursor-pointer list-none items-center gap-3 p-2.5 sm:px-4 [&::-webkit-details-marker]:hidden">
          <div className="flex min-w-0 grow shrink-0 basis-24 flex-col gap-0.5">
            {title}
            {meta}
          </div>
          <div className="flex min-w-0 flex-wrap items-center justify-end gap-1.5">
            {badges}
            <ChevronDown
              className="text-muted-foreground size-4 transition-transform group-open:rotate-180"
              aria-hidden
            />
          </div>
        </summary>
        <div className="border-border flex flex-col gap-3 border-t p-3 sm:px-4">{children}</div>
      </details>
    </Card>
  )
}


/** Simple definition list used by the detail pages. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-1.5 sm:flex-row sm:items-baseline sm:gap-3">
      <span className="text-muted-foreground w-40 shrink-0 text-xs">{label}</span>
      <span className="text-sm break-all">{children}</span>
    </div>
  )
}
